import Storage from '~/util/storage'

function normalizeNotificationsList(list) {
	return Array.isArray(list) ? list : []
}

function cloneNotifications(list = []) {
	return normalizeNotificationsList(list).map(notification => ({
		...notification,
		data: notification.data ? { ...notification.data } : notification.data
	}))
}

function defaultPaginationState() {
	return {
		nextCursor: null
	}
}

function removeNotificationById(list = [], notificationId) {
	return normalizeNotificationsList(list).filter(
		notification => notification.id !== notificationId
	)
}

function mergeNotifications(existing = [], incoming = []) {
	const existingItems = normalizeNotificationsList(existing)
	const seenIds = new Set(existingItems.map(notification => notification.id))
	const merged = [...existingItems]

	normalizeNotificationsList(incoming).forEach(notification => {
		if (!seenIds.has(notification.id)) {
			merged.push(notification)
			seenIds.add(notification.id)
		}
	})

	return merged
}

function isCurrentSession(state, sessionGeneration) {
	return state.sessionGeneration === sessionGeneration
}

export const getDefaultState = () => {
	return {
		isSlideoutOpen: false,
		notificationsPermission: Storage.get('notifications-permission'),
		activeTab: 'unread',
		itemsByTab: {
			unread: [],
			read: []
		},
		paginationByTab: {
			unread: defaultPaginationState(),
			read: defaultPaginationState()
		},
		unreadCount: 0,
		sessionGeneration: 0,
		loading: {
			list: false,
			count: false,
			markRead: false,
			dismiss: false,
			readAll: false,
			dismissAll: false
		}
	}
}

export const state = () => getDefaultState()

export const getters = {
	currentTabItems(state) {
		return normalizeNotificationsList(state.itemsByTab?.[state.activeTab])
	},
	currentTabNextCursor(state) {
		return state.paginationByTab[state.activeTab]?.nextCursor || null
	}
}

export const mutations = {
	RESET_STATE(state) {
		const nextSessionGeneration = Number(state.sessionGeneration || 0) + 1

		Object.assign(state, getDefaultState(), {
			sessionGeneration: nextSessionGeneration
		})
	},
	setIsSlideoutOpen(state, open) {
		state.isSlideoutOpen = open
	},
	setNotificationsPermission(state, permission) {
		state.notificationsPermission = permission
		Storage.set('notifications-permission', permission)
	},
	setActiveTab(state, tab) {
		state.activeTab = tab
	},
	setItemsForTab(state, { tab, items = [] }) {
		state.itemsByTab = {
			...state.itemsByTab,
			[tab]: normalizeNotificationsList(items)
		}
	},
	setPaginationForTab(state, { tab, nextCursor = null }) {
		state.paginationByTab = {
			...state.paginationByTab,
			[tab]: {
				nextCursor
			}
		}
	},
	setUnreadCount(state, count) {
		state.unreadCount = count
	},
	setLoading(state, { key, value }) {
		state.loading = {
			...state.loading,
			[key]: value
		}
	}
}

export const actions = {
	fetchCount({ commit, rootState, state }) {
		if (!rootState.user.token) {
			return Promise.reject(new Error('No authentication token'))
		}

		const sessionGeneration = state.sessionGeneration
		commit('setLoading', { key: 'count', value: true })

		return this.$axios
			.$get('user/notification/count', {
				__skip404Toast: true
			})
			.then(response => {
				if (isCurrentSession(state, sessionGeneration)) {
					commit('setUnreadCount', response.unread || 0)
				}
				return response
			})
			.catch(error => {
				if (
					isCurrentSession(state, sessionGeneration) &&
					error.response &&
					error.response.status === 401
				) {
					commit('setUnreadCount', 0)
				}

				return Promise.reject(error)
			})
			.finally(() => {
				if (isCurrentSession(state, sessionGeneration)) {
					commit('setLoading', { key: 'count', value: false })
				}
			})
	},
	fetch({ commit, rootState, state }, payload = {}) {
		if (!rootState.user.token) {
			return Promise.reject(new Error('No authentication token'))
		}

		const tab = payload.state || 'unread'
		const append = Boolean(payload.append)
		const sessionGeneration = state.sessionGeneration

		commit('setLoading', { key: 'list', value: true })

		return this.$axios
			.$get('user/notification', {
				params: {
					state: tab,
					limit: payload.limit || 15,
					cursor: payload.cursor || null
				},
				__skip404Toast: true
			})
			.then(response => {
				if (!isCurrentSession(state, sessionGeneration)) {
					return response
				}

				const hasValidItems = Array.isArray(response?.data)
				const incomingItems = normalizeNotificationsList(response?.data)
				commit('setItemsForTab', {
					tab,
					items: append
						? mergeNotifications(
								state.itemsByTab[tab] || [],
								incomingItems
						  )
						: incomingItems
				})
				commit('setPaginationForTab', {
					tab,
					nextCursor: hasValidItems
						? response.meta?.next_cursor || null
						: null
				})
				return response
			})
			.catch(error => {
				if (
					isCurrentSession(state, sessionGeneration) &&
					error.response &&
					error.response.status === 401
				) {
					commit('setItemsForTab', {
						tab,
						items: []
					})
					commit('setPaginationForTab', {
						tab,
						nextCursor: null
					})
				}

				return Promise.reject(error)
			})
			.finally(() => {
				if (isCurrentSession(state, sessionGeneration)) {
					commit('setLoading', { key: 'list', value: false })
				}
			})
	},
	setActiveTab({ commit }, tab) {
		commit('setActiveTab', tab)
	},
	markRead({ commit, state }, notification) {
		const sessionGeneration = state.sessionGeneration
		const unreadBefore = cloneNotifications(state.itemsByTab.unread)
		const readBefore = cloneNotifications(state.itemsByTab.read)
		const unreadCountBefore = state.unreadCount
		const updatedNotification = {
			...notification,
			read_at: new Date().toISOString()
		}

		commit('setLoading', { key: 'markRead', value: true })
		commit('setItemsForTab', {
			tab: 'unread',
			items: removeNotificationById(unreadBefore, notification.id)
		})
		commit('setItemsForTab', {
			tab: 'read',
			items: [
				updatedNotification,
				...removeNotificationById(readBefore, notification.id)
			]
		})
		commit('setUnreadCount', Math.max(0, unreadCountBefore - 1))

		return this.$axios
			.$post(`user/notification/${notification.id}/read`)
			.catch(error => {
				if (isCurrentSession(state, sessionGeneration)) {
					commit('setItemsForTab', {
						tab: 'unread',
						items: unreadBefore
					})
					commit('setItemsForTab', {
						tab: 'read',
						items: readBefore
					})
					commit('setUnreadCount', unreadCountBefore)
				}
				return Promise.reject(error)
			})
			.finally(() => {
				if (isCurrentSession(state, sessionGeneration)) {
					commit('setLoading', { key: 'markRead', value: false })
				}
			})
	},
	dismiss({ commit, state }, notification) {
		const sessionGeneration = state.sessionGeneration
		const unreadBefore = cloneNotifications(state.itemsByTab.unread)
		const readBefore = cloneNotifications(state.itemsByTab.read)
		const unreadCountBefore = state.unreadCount
		const wasUnread = !notification.read_at

		commit('setLoading', { key: 'dismiss', value: true })
		commit('setItemsForTab', {
			tab: 'unread',
			items: removeNotificationById(unreadBefore, notification.id)
		})
		commit('setItemsForTab', {
			tab: 'read',
			items: removeNotificationById(readBefore, notification.id)
		})

		if (wasUnread) {
			commit('setUnreadCount', Math.max(0, unreadCountBefore - 1))
		}

		return this.$axios
			.$delete(`user/notification/${notification.id}`)
			.catch(error => {
				if (isCurrentSession(state, sessionGeneration)) {
					commit('setItemsForTab', {
						tab: 'unread',
						items: unreadBefore
					})
					commit('setItemsForTab', {
						tab: 'read',
						items: readBefore
					})
					commit('setUnreadCount', unreadCountBefore)
				}
				return Promise.reject(error)
			})
			.finally(() => {
				if (isCurrentSession(state, sessionGeneration)) {
					commit('setLoading', { key: 'dismiss', value: false })
				}
			})
	},
	readAll({ commit, state }) {
		const sessionGeneration = state.sessionGeneration
		const unreadBefore = cloneNotifications(state.itemsByTab.unread)
		const readBefore = cloneNotifications(state.itemsByTab.read)
		const unreadCountBefore = state.unreadCount
		const unreadPaginationBefore =
			state.paginationByTab.unread?.nextCursor || null
		const movedNotifications = unreadBefore.map(notification => ({
			...notification,
			read_at: new Date().toISOString()
		}))

		commit('setLoading', { key: 'readAll', value: true })
		commit('setItemsForTab', {
			tab: 'unread',
			items: []
		})
		commit('setPaginationForTab', {
			tab: 'unread',
			nextCursor: null
		})
		commit('setItemsForTab', {
			tab: 'read',
			items: [...movedNotifications, ...readBefore]
		})
		commit('setUnreadCount', 0)

		return this.$axios
			.$post('user/notification/read-all')
			.catch(error => {
				if (isCurrentSession(state, sessionGeneration)) {
					commit('setItemsForTab', {
						tab: 'unread',
						items: unreadBefore
					})
					commit('setPaginationForTab', {
						tab: 'unread',
						nextCursor: unreadPaginationBefore
					})
					commit('setItemsForTab', {
						tab: 'read',
						items: readBefore
					})
					commit('setUnreadCount', unreadCountBefore)
				}
				return Promise.reject(error)
			})
			.finally(() => {
				if (isCurrentSession(state, sessionGeneration)) {
					commit('setLoading', { key: 'readAll', value: false })
				}
			})
	},
	dismissAll({ commit, state }, payload = {}) {
		const sessionGeneration = state.sessionGeneration
		const scope = payload.state || 'all'
		const unreadBefore = cloneNotifications(state.itemsByTab.unread)
		const readBefore = cloneNotifications(state.itemsByTab.read)
		const unreadCountBefore = state.unreadCount
		const unreadPaginationBefore =
			state.paginationByTab.unread?.nextCursor || null
		const readPaginationBefore =
			state.paginationByTab.read?.nextCursor || null
		let unreadAfter = unreadBefore
		let readAfter = readBefore
		let unreadCountAfter = unreadCountBefore

		if (scope === 'unread') {
			unreadAfter = []
			unreadCountAfter = 0
			commit('setPaginationForTab', {
				tab: 'unread',
				nextCursor: null
			})
		} else if (scope === 'read') {
			readAfter = []
			commit('setPaginationForTab', {
				tab: 'read',
				nextCursor: null
			})
		} else {
			unreadAfter = []
			readAfter = []
			unreadCountAfter = 0
			commit('setPaginationForTab', {
				tab: 'unread',
				nextCursor: null
			})
			commit('setPaginationForTab', {
				tab: 'read',
				nextCursor: null
			})
		}

		commit('setLoading', { key: 'dismissAll', value: true })
		commit('setItemsForTab', {
			tab: 'unread',
			items: unreadAfter
		})
		commit('setItemsForTab', {
			tab: 'read',
			items: readAfter
		})
		commit('setUnreadCount', unreadCountAfter)

		return this.$axios
			.$delete('user/notification', {
				params: {
					state: scope
				}
			})
			.catch(error => {
				if (isCurrentSession(state, sessionGeneration)) {
					commit('setItemsForTab', {
						tab: 'unread',
						items: unreadBefore
					})
					commit('setItemsForTab', {
						tab: 'read',
						items: readBefore
					})
					commit('setPaginationForTab', {
						tab: 'unread',
						nextCursor: unreadPaginationBefore
					})
					commit('setPaginationForTab', {
						tab: 'read',
						nextCursor: readPaginationBefore
					})
					commit('setUnreadCount', unreadCountBefore)
				}
				return Promise.reject(error)
			})
			.finally(() => {
				if (isCurrentSession(state, sessionGeneration)) {
					commit('setLoading', { key: 'dismissAll', value: false })
				}
			})
	},
	toggleSlideout({ commit, state }) {
		commit('setIsSlideoutOpen', !state.isSlideoutOpen)
	}
}
